import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { Vector3 } from '@kitware/vtk.js/types';

import catalogJson from '@/src/assets/implants/catalog.json';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import type { DentalArch } from '@/src/io/state-file/schema';
import { Manifest, StateFile } from '@/src/io/state-file/schema';
import { useIdStore } from '@/src/store/id';
import { useCurvedMPRStore } from '@/src/store/tools/curvedMPR';
import type {
  ImplantCatalogEntry,
  PlacedImplant,
} from '@/src/utils/implantPlanningGeometry';
import { normalize } from '@/src/utils/implantPlanningGeometry';

export const useImplantPlanningStore = defineStore('implantPlanning', () => {
  const catalog = catalogJson as ImplantCatalogEntry[];
  const selectedCatalogId = ref<string | null>(catalog[0]?.id ?? null);
  const placementMode = ref(false);
  /** True while dragging an implant in a 2D view (disables arch spline). */
  const isManipulating = ref(false);
  const implants = ref<PlacedImplant[]>([]);
  const activeImplantId = ref<string | null>(null);

  const { currentImageID } = useCurrentImage('global');
  let lastImageID: string | null | undefined = undefined;

  const selectedCatalogEntry = computed(() =>
    catalog.find((e) => e.id === selectedCatalogId.value)
  );

  const curvedMPR = useCurvedMPRStore();

  const implantsOnActiveArch = computed(() =>
    implants.value.filter((i) => i.arch === curvedMPR.activeArch)
  );

  function selectCatalog(id: string) {
    selectedCatalogId.value = id;
  }

  function setPlacementMode(on: boolean) {
    placementMode.value = on;
    if (on) isManipulating.value = false;
  }

  function setManipulating(on: boolean) {
    isManipulating.value = on;
  }

  function setActiveImplant(id: string | null) {
    activeImplantId.value = id;
  }

  function addImplant(pose: {
    origin: Vector3;
    axis: Vector3;
    catalogId?: string;
    arch?: DentalArch;
  }): PlacedImplant | null {
    const catId = pose.catalogId ?? selectedCatalogId.value;
    if (!catId) return null;
    const entry = catalog.find((c) => c.id === catId);
    if (!entry) return null;

    const implant: PlacedImplant = {
      id: useIdStore().nextId(),
      catalogId: catId,
      origin: [pose.origin[0], pose.origin[1], pose.origin[2]],
      axis: normalize(pose.axis),
      arch: pose.arch ?? curvedMPR.activeArch,
    };
    implants.value.push(implant);
    activeImplantId.value = implant.id;
    return implant;
  }

  function updateImplantPose(
    id: string,
    patch: Partial<Pick<PlacedImplant, 'origin' | 'axis'>>
  ) {
    const idx = implants.value.findIndex((i) => i.id === id);
    if (idx < 0) return;
    const cur = implants.value[idx];
    implants.value[idx] = {
      ...cur,
      origin: patch.origin
        ? ([patch.origin[0], patch.origin[1], patch.origin[2]] as Vector3)
        : cur.origin,
      axis: patch.axis ? normalize(patch.axis) : cur.axis,
    };
  }

  function removeImplant(id: string) {
    implants.value = implants.value.filter((i) => i.id !== id);
    if (activeImplantId.value === id) {
      activeImplantId.value = implants.value[0]?.id ?? null;
    }
  }

  function getCatalogEntry(catalogId: string): ImplantCatalogEntry | undefined {
    return catalog.find((c) => c.id === catalogId);
  }

  function clearAll() {
    implants.value = [];
    activeImplantId.value = null;
    placementMode.value = false;
  }

  function exportForSerialize(): PlacedImplant[] {
    return implants.value.map((i) => ({
      id: i.id,
      catalogId: i.catalogId,
      origin: [i.origin[0], i.origin[1], i.origin[2]] as Vector3,
      axis: [i.axis[0], i.axis[1], i.axis[2]] as Vector3,
      arch: i.arch,
    }));
  }

  function importFromDeserialize(list: PlacedImplant[]) {
    implants.value = list.map((i) => ({
      id: i.id,
      catalogId: i.catalogId,
      origin: [i.origin[0], i.origin[1], i.origin[2]] as Vector3,
      axis: normalize(i.axis),
      arch: i.arch,
    }));
    activeImplantId.value = implants.value[0]?.id ?? null;
  }

  watch(
    () => currentImageID.value,
    (id) => {
      if (lastImageID === undefined) {
        lastImageID = id;
        return;
      }
      if (id !== lastImageID) {
        clearAll();
        lastImageID = id;
      }
    },
    { immediate: true }
  );

  function serialize(_state: StateFile) {
    // Written via curvedMPR.serialize
  }

  function deserialize(_manifest: Manifest) {
    // Loaded via curvedMPR.deserialize
  }

  return {
    catalog,
    selectedCatalogId,
    selectedCatalogEntry,
    placementMode,
    isManipulating,
    implants,
    implantsOnActiveArch,
    activeImplantId,
    selectCatalog,
    setPlacementMode,
    setManipulating,
    setActiveImplant,
    addImplant,
    updateImplantPose,
    removeImplant,
    getCatalogEntry,
    clearAll,
    exportForSerialize,
    importFromDeserialize,
    serialize,
    deserialize,
  };
});
